/**
 * Portal Manta · ComparativoLotesPage
 * Extraído de App.jsx (v29 modular)
 */
import React, { useState, useEffect, useMemo, useCallback, Fragment, memo } from 'react';
import { DADOS } from '../../data/lotes-pr.js';
import { V, S, fmtNum, sumBy, fmtBRL, OBRAS_ROD } from '../../globals.js';
import { CONSOL_FIN } from '../../data/consol-fin.js';
import { SectionCollapse } from '../lotes-tabs/SectionCollapse.jsx';
import { M } from '../../tokens/colors.js';
import { THl, TH, TDl, TD } from '../../app-globals.js';

const ComparativoLotesPage = memo(function ComparativoLotesPage_Inner(){
  const lotes = ['3','4','5'];
  const cores = {'3':V.gn,'4':V.bl,'5':V.or};
  const dados = lotes.map(sig=>{
    const d = DADOS[sig]||{};
    const od = OBRAS_ROD[sig]||{};
    const cf = CONSOL_FIN[sig]||{};
    const ext = parseFloat((d.extensao||'').replace(/[^\d,.]/g,'').replace(',','.'))||0;
    const totSicro = Object.values(cf).reduce((s,arr)=>s+sumBy(arr,'v'),0);
    // Quantitativos físicos
    const dupKm = sumBy(od.duplicacoes||[],'km')+sumBy(od.contornos||[],'km');
    const fxKm = sumBy(od.faixas||[],'km');
    const margKm = sumBy(od.marginais||[],'km');
    const cicKm = sumBy(od.ciclovias||[],'km');
    const oaeN = sumBy(od.oaes||[],'n');
    const passN = sumBy(od.passarelas||[],'n');
    const interN = sumBy(od.diamantes||[],'n')+sumBy(od.trombetas||[],'n')
                  +sumBy(od.parclos||[],'n')+sumBy(od.rotatorias||[],'n');
    const nRods = (od.sistema||[]).length;
    // SICRO por disciplina
    const pavSicro = sumBy(cf['PAVIMENTACAO']||[],'v');
    const terSicro = sumBy(cf['TERRAPLENOS E CONTENCOES']||[],'v');
    const drnSicro = sumBy(cf['DRENAGEM']||[],'v');
    const oaeSicro = sumBy(cf['OAES']||[],'v');
    const sinSicro = sumBy(cf['SINALIZACAO']||[],'v');
    const iluSicro = sumBy(cf['ILUMINACAO']||[],'v');
    return {
      sig, nome:d.nome, ext, capex:d.capex_total_b||0, opex:d.opex_total_b||0,
      tir:d.tir||0, totSicro, nRods,
      dupKm, fxKm, margKm, cicKm, oaeN, passN, interN,
      // Densidades (por km)
      dupPct: ext?dupKm/ext*100:0,
      fxPct:  ext?fxKm/ext*100:0,
      margDens: ext?margKm/ext*100:0,
      cicDens: ext?cicKm/ext*100:0,
      oaeDens: ext?oaeN/ext:0,         // OAE/km
      passDens: ext?passN/ext*10:0,    // passar/10km
      interDens: ext?interN/ext*10:0,  // inter/10km
      rkmSicro: ext?totSicro/ext:0,
      pavSicro, terSicro, drnSicro, oaeSicro, sinSicro, iluSicro,
      pavPct: totSicro?pavSicro/totSicro*100:0,
      terPct: totSicro?terSicro/totSicro*100:0,
      drnPct: totSicro?drnSicro/totSicro*100:0,
      oaePct: totSicro?oaeSicro/totSicro*100:0,
    };
  });
  // Classificação qualitativa: helper para cor
  const classify = (val, low, high)=> val<low?'BAIXA':(val>high?'ALTA':'MÉDIA');
  const corClass = c => c==='ALTA'?V.or:(c==='MÉDIA'?V.am||'#d97706':V.gn);
  // Indicadores
  const indicadores = [
    {l:'Extensão', fmt:r=>fmtNum(r.ext,1)+' km', desc:'Total da rodovia concedida'},
    {l:'Nº Rodovias', fmt:r=>r.nRods, desc:'Quantidade de rodovias do sistema'},
    {l:'CAPEX', fmt:r=>'R$ '+fmtNum(r.capex,2)+' bi', desc:'Total de investimento (30 anos)'},
    {l:'Custo SICRO', fmt:r=>fmtBRL(r.totSicro,0), desc:'Σ disciplinas (consolidado_financeiro)'},
    {l:'R$ médio/km', fmt:r=>fmtBRL(r.rkmSicro,0)+'/km', desc:'Custo médio SICRO por km',
     classify:r=>classify(r.rkmSicro/1e6, 1.2, 1.5)},
    {l:'TIR (Taxa Interna)', fmt:r=>(r.tir*100).toFixed(2)+'%', desc:'Taxa do leilão'},
    {sep:'INTENSIDADE DE OBRAS'},
    {l:'Duplicação (% trecho)', fmt:r=>r.dupPct.toFixed(1)+'%', desc:'km duplicado / extensão',
     classify:r=>classify(r.dupPct,15,30)},
    {l:'Faixas adicionais (% trecho)', fmt:r=>r.fxPct.toFixed(1)+'%', desc:'km de faixas adicionais / extensão',
     classify:r=>classify(r.fxPct,1,3)},
    {l:'Marginais', fmt:r=>fmtNum(r.margKm,1)+' km ('+r.margDens.toFixed(1)+'%)', desc:'Indicador de contexto periurbano',
     classify:r=>classify(r.margDens,0.5,2)},
    {l:'Ciclovias', fmt:r=>fmtNum(r.cicKm,1)+' km', desc:'Indicador de adensamento urbano',
     classify:r=>classify(r.cicKm,5,15)},
    {sep:'PONTOS NOTÁVEIS (densidade)'},
    {l:'OAEs (un)', fmt:r=>r.oaeN+' un · '+r.oaeDens.toFixed(2)+'/km', desc:'Pontes/viadutos. Alta = travessias frequentes',
     classify:r=>classify(r.oaeDens,0.3,0.5)},
    {l:'Passarelas (un)', fmt:r=>r.passN+' un', desc:'Travessia de pedestres em áreas urbanas',
     classify:r=>classify(r.passN,15,30)},
    {l:'Interseções (un)', fmt:r=>r.interN+' un', desc:'Diamantes+trombetas+parclos+rotatórias',
     classify:r=>classify(r.interN,30,60)},
    {sep:'COMPOSIÇÃO DO CUSTO SICRO'},
    {l:'% Pavimentação', fmt:r=>r.pavPct.toFixed(1)+'%', desc:'Domínio do orçamento',
     classify:r=>classify(r.pavPct,30,45)},
    {l:'% Terraplenos & Contenções', fmt:r=>r.terPct.toFixed(1)+'%', desc:'Indicador de geotecnia/relevo',
     classify:r=>classify(r.terPct,15,25)},
    {l:'% Drenagem', fmt:r=>r.drnPct.toFixed(1)+'%', desc:'Cursos d\'água, infiltração',
     classify:r=>classify(r.drnPct,10,20)},
    {l:'% OAEs', fmt:r=>r.oaePct.toFixed(1)+'%', desc:'Travessias estruturais',
     classify:r=>classify(r.oaePct,2,8)},
  ];
  return(<div style={S.page}>
    <div style={S.h1}>Fatores Qualitativos × Orçamento · Comparativo L3 · L4 · L5</div>
    <div style={S.sub}>
      KPIs derivados das quantidades físicas (PER + OBRAS_ROD) e do consolidado SICRO. 
      Classificação <b style={{color:V.gn}}>BAIXA</b> · <b style={{color:V.am||'#d97706'}}>MÉDIA</b> · <b style={{color:V.or}}>ALTA</b> em relação aos 3 lotes.
    </div>
    {/* Tabela comparativa */}
    <div style={{...S.card,overflow:'hidden'}}>
      <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
        <thead style={S.thRow}><tr style={{borderBottom:'2px solid '+V.b1}}>
          <th style={{...THl,width:'30%'}}>Indicador</th>
          {dados.map(r=>(<th key={r.sig} style={{...TH,background:cores[r.sig],color:'#fff'}}>{r.nome}</th>))}
          <th style={{...THl,width:'25%'}}>Descrição/Sentido</th>
        </tr></thead>
        <tbody>
          {indicadores.map((ind,i)=>{
            if(ind.sep) return(<tr key={'s'+i}><td colSpan={5} style={{padding:'10px 10px 4px',fontSize:10,fontWeight:700,letterSpacing:.5,color:V.gr,background:V.g0}}>{ind.sep}</td></tr>);
            return(<tr key={i} style={S.trLine}>
              <td style={{...TDl,fontWeight:600}}>{ind.l}</td>
              {dados.map(r=>{
                const klass = ind.classify ? ind.classify(r) : null;
                return(<td key={r.sig} style={{...TD,borderLeft:'2px solid '+cores[r.sig]+'33'}}>
                  <span style={{fontWeight:600,color:V.br}}>{ind.fmt(r)}</span>
                  {klass && <span style={{display:'block',fontSize:9,fontWeight:700,color:corClass(klass),marginTop:2}}>● {klass}</span>}
                </td>);
              })}
              <td style={{...TDl,fontSize:10,color:V.gr}}>{ind.desc}</td>
            </tr>);
          })}
        </tbody>
      </table>
    </div>
    {/* Síntese narrativa */}
    <SectionCollapse id="sintese" titulo="Síntese narrativa do impacto qualitativo no orçamento" defOpen>
    <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10}}>
      {dados.map(r=>{
        const intens = (r.dupPct + r.fxPct*5 + r.cicKm/5 + r.oaeDens*100 + r.interN/r.ext*10);
        const lvl = intens>40?'COMPLEXIDADE ALTA':(intens>25?'COMPLEXIDADE MÉDIA':'COMPLEXIDADE BAIXA');
        return(<div key={r.sig} style={{...S.cardP,borderTop:'4px solid '+cores[r.sig]}}>
          <div style={{fontSize:10,fontWeight:700,letterSpacing:.4,color:cores[r.sig]}}>{r.nome}</div>
          <div style={{fontSize:13,fontWeight:700,color:V.br,marginTop:6}}>{lvl}</div>
          <div style={{fontSize:11,color:V.gr,marginTop:8,lineHeight:1.45}}>
            <div>· <b>{r.nRods} rodovias</b> · {fmtNum(r.ext,1)} km</div>
            <div>· <b>{r.dupPct.toFixed(1)}%</b> com duplicação</div>
            <div>· <b>{r.oaeN} OAEs</b> ({r.oaeDens.toFixed(2)}/km)</div>
            <div>· <b>{r.interN} interseções</b> + {r.passN} passarelas</div>
            <div>· <b>{fmtNum(r.cicKm,1)} km</b> de ciclovia · <b>{fmtNum(r.margKm,1)} km</b> de marginais</div>
            <div style={{marginTop:6,paddingTop:6,borderTop:'1px solid '+V.b1}}>
              R$/km: <b style={{color:V.br}}>{fmtBRL(r.rkmSicro,0)}</b> · 
              Pav. <b>{r.pavPct.toFixed(0)}%</b> · 
              Terra. <b>{r.terPct.toFixed(0)}%</b> · 
              OAE <b>{r.oaePct.toFixed(0)}%</b>
            </div>
          </div>
        </div>);
      })}
    </div>
    </SectionCollapse>
    <SectionCollapse id="fonte" titulo="Sobre os dados" defOpen={false}>
    <div style={{...S.cardP,fontSize:10.5,color:V.gr,lineHeight:1.6}}>
      <p>Os indicadores são derivados dos dados embutidos no artefato:</p>
      <ul style={{paddingLeft:20,marginTop:6}}>
        <li><b>DADOS[sig]</b> — extensão, CAPEX, OPEX, TIR (do MEF/Edital)</li>
        <li><b>OBRAS_ROD[sig]</b> — duplicações, faixas, marginais, ciclovias, OAEs, passarelas, interseções por rodovia</li>
        <li><b>CONSOL_FIN[sig]</b> — orçamento SICRO c/BDI consolidado por disciplina (562 itens · 7 disciplinas)</li>
      </ul>
      <p style={{marginTop:8}}>
        As classificações <b style={{color:V.gn}}>BAIXA</b>/<b style={{color:V.am||'#d97706'}}>MÉDIA</b>/<b style={{color:V.or}}>ALTA</b> usam thresholds calibrados pela mediana dos 3 lotes. 
        Indicadores qualitativos (contexto urbano, geotecnia detalhada, relevo) requerem leitura dos PER Vol II e MEF — disponíveis no SharePoint mas não embutidos aqui para manter o tamanho do artefato sob limite.
      </p>
    </div>
    </SectionCollapse>
  </div>);
});

export { ComparativoLotesPage };
export default ComparativoLotesPage;
